import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { peekLoginContinuation } from '@/components/auth/loginContinuation';
import { useAuthContext, useLogin } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { ApiError } from '@/lib/api';
import { ProfileDialog } from '@/components/profile/ProfileDialog';
import { AuthCard } from './AuthCard';

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

type LoginForm = z.infer<typeof loginSchema>;

const LOGIN_REASONS = ['inactivity'] as const;
type LoginReason = (typeof LOGIN_REASONS)[number];

function loginReasonFrom(value: string | null): LoginReason | null {
  return LOGIN_REASONS.includes(value as LoginReason) ? (value as LoginReason) : null;
}

/**
 * Where sign-in actually begins. A server route, not a portal one: it mints the
 * PKCE transaction before the browser ever reaches the provider.
 */
const PAYA_ID_START = '/api/v1/auth/oidc/start';

export default function LoginPage() {
  const { t } = useTranslation();
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [continuation] = useState(() => peekLoginContinuation());
  const urlReason = loginReasonFrom(searchParams.get('reason'));
  const loginReason = urlReason ?? loginReasonFrom(continuation?.reason ?? null);
  const loginMutation = useLogin();

  useEffect(() => {
    const returnTo = continuation?.returnTo;
    if (returnTo === null || returnTo === undefined) return;
    useAuthStore.getState().setRedirectAfterLogin(returnTo);
  }, [continuation]);

  // Whether this hostname names a tenant, and whether Paya ID is wired. Both
  // are server-side facts the browser cannot work out from the URL alone.
  const { data: authContext } = useAuthContext();
  // Apex means BOTH: hostnames name tenants here, and this one does not. A
  // single-tenant environment also has no tenant on the host, and reading that
  // as the apex hides the password form and locks everyone out (#1694).
  //
  // Undefined while loading: keep the password form until the answer arrives,
  // so a tenant host never flashes an apex-only page on a slow connection.
  const onApex = authContext ? authContext.multiTenant && authContext.tenant === null : false;
  const payaIdEnabled = authContext?.oidcEnabled ?? false;

  /**
   * Whether to offer an OS Buddy password at all (#1787).
   *
   * Two ways in is one too many when the second is the one we do not want used
   * and #1628 deletes outright. Hidden only where Paya ID actually works: the
   * same portal image serves five environments that have none (#1766), and
   * there the form is the only way in — so this is read from the deployment,
   * never from a build-time flag.
   *
   * A user without a principal in a Paya ID environment would be locked out by
   * this. Running the link migration is a step of every #1766 rollout issue and
   * precedes turning `oidcEnabled` on.
   */
  const showPasswordForm = !onApex && !payaIdEnabled;

  /**
   * Nothing to choose here — go straight to Paya ID (#2227).
   *
   * When Paya ID is the only way in, this page is a single button in front of
   * the real sign-in. Skipping it takes one of three steps off every login.
   *
   * Waits for `authContext`: while it is loading `showPasswordForm` is `true`,
   * and acting on that would send the five environments without Paya ID (#1766)
   * to a provider they do not have.
   *
   * Deliberately **not** done until #2109 shipped. Before that, logout left the
   * Paya ID session standing, and a page that redirects on sight would have
   * made signing out impossible: the browser lands here and is thrown straight
   * back in. The button below stays as the fallback for a browser that does not
   * follow this — it is a real link, not an `onClick`.
   *
   * Validated inactivity (#2212) is the exception: auto-assign would hop through
   * the provider before the user can read why they were signed out. Peek the
   * continuation (do not consume it before the provider returns) and keep the
   * safe return path. Unknown `?reason=` values are not this exception.
   */
  const payaIdIsTheOnlyWayIn = authContext !== undefined && payaIdEnabled && !showPasswordForm;
  const skipPayaIdAutoRedirect = loginReason === 'inactivity';

  useEffect(() => {
    if (!payaIdIsTheOnlyWayIn || skipPayaIdAutoRedirect) return;
    window.location.assign(PAYA_ID_START);
  }, [payaIdIsTheOnlyWayIn, skipPayaIdAutoRedirect]);
  const [error, setError] = useState<string | null>(null);
  const [showForcedPasswordChange, setShowForcedPasswordChange] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setError(null);

    try {
      await loginMutation.mutateAsync({
        email: data.email,
        password: data.password,
      });

      const user = useAuthStore.getState().user;
      if (user?.forcePasswordChange) {
        setShowForcedPasswordChange(true);
      } else {
        const target = useAuthStore.getState().consumeRedirectAfterLogin() ?? '/';
        void navigate(target, { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        const errorData = err.data as { message?: string } | undefined;
        setError(errorData?.message ?? t('login.invalidCredentials'));
      } else {
        setError(t('errors.generic'));
      }
    }
  };

  const handleForcedPasswordChanged = () => {
    setShowForcedPasswordChange(false);
    const target = useAuthStore.getState().consumeRedirectAfterLogin() ?? '/';
    void navigate(target, { replace: true });
  };

  return (
    <>
      <AuthCard>
        {loginReason === 'inactivity' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {tc('session.loggedOutInactivity')}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {payaIdEnabled && (
          <Button
            variant="contained"
            fullWidth
            size="large"
            href={PAYA_ID_START}
            sx={{ mb: showPasswordForm ? 3 : 0 }}
          >
            {t('login.payaId')}
          </Button>
        )}

        {onApex ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
            {payaIdEnabled ? t('login.apexHint') : t('login.apexNoSignIn')}
          </Typography>
        ) : (
          showPasswordForm && (
            <form onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
              <TextField
                {...register('email')}
                label={t('login.email')}
                type="email"
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
                sx={{ mb: 2 }}
              />
              <TextField
                {...register('password')}
                label={t('login.password')}
                type="password"
                fullWidth
                error={!!errors.password}
                helperText={errors.password?.message}
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loginMutation.isPending}
              >
                {loginMutation.isPending ? <CircularProgress size={24} /> : t('login.submit')}
              </Button>
            </form>
          )
        )}

        {onApex && (
          // Self-service signup (#3017): only the apex has no workshop of its own to sign in to.
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            {t('login.signupPrompt')}{' '}
            <Link href="/signup" underline="hover">
              {t('login.signupLink')}
            </Link>
          </Typography>
        )}

        {showPasswordForm && (
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              onClick={() => navigate('/forgot-password')}
              sx={{ color: 'text.secondary' }}
            >
              {tc('resetPassword.forgotPassword')}
            </Link>
          </Box>
        )}
      </AuthCard>

      <ProfileDialog
        open={showForcedPasswordChange}
        onClose={() => {/* Cannot close forced dialog */}}
        forced
        onForcedPasswordChanged={handleForcedPasswordChanged}
      />
    </>
  );
}
